<template>
  <Sheet :open="open" @update:open="onOpenChange">
    <SheetContent
      :side="side"
      :show-close="showClose"
      :class="cn(sizeClass, 'p-0', isResizing && 'select-none')"
      @escape-key-down="onEscapeKeyDown"
      @pointer-down-outside="onPointerDownOutside"
    >
      <div
        v-if="resizable"
        ref="handleRef"
        class="jdrawer-resize-handle"
        :class="handlePositionClass"
        @pointerdown="onHandlePointerDown"
      />

      <slot name="header">
        <SheetHeader v-if="title || description || $slots['title-suffix'] || $slots.description">
          <SheetTitle v-if="title" class="flex items-center gap-2">
            <span>{{ title }}</span>
            <slot name="title-suffix" />
          </SheetTitle>
          <SheetDescription v-if="description || $slots.description">
            <slot name="description">{{ description }}</slot>
          </SheetDescription>
        </SheetHeader>
      </slot>

      <div class="flex-1 overflow-auto px-6 py-4">
        <slot />
      </div>

      <slot name="footer">
        <SheetFooter v-if="showFooter" :class="$slots['footer-extra'] ? 'justify-between' : ''">
          <slot name="footer-extra" />
          <div class="flex gap-2">
            <JButton
              v-if="buttonType === 'OkCancel'"
              variant="outline"
              size="sm"
              @click="handleCancel"
            >
              {{ cancelText }}
            </JButton>
            <JButton
              :variant="confirmVariant"
              :disabled="confirmDisabled"
              size="sm"
              @click="handleConfirm"
            >
              {{ confirmText }}
            </JButton>
          </div>
        </SheetFooter>
      </slot>
    </SheetContent>
  </Sheet>
</template>

<script setup lang="ts">
import { computed, nextTick, onUnmounted, ref, watch } from 'vue'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/shadcn'
import type { ButtonVariants, SheetSide } from '@/components/shadcn'
import { JButton } from '@/components/atoms'
import { cn } from '@/lib/utils'

export interface JDrawerProps {
  open: boolean
  side?: SheetSide
  size?: 'sm' | 'md' | 'lg' | 'full'
  title?: string
  description?: string
  showClose?: boolean
  showFooter?: boolean
  buttonType?: 'Ok' | 'OkCancel'
  confirmText?: string
  cancelText?: string
  confirmVariant?: ButtonVariants['variant']
  confirmDisabled?: boolean
  closeOnOverlayClick?: boolean
  closeOnEscape?: boolean
  resizable?: boolean
  minSize?: number
  maxSize?: number
  defaultSize?: number
}

const props = withDefaults(defineProps<JDrawerProps>(), {
  side: 'right',
  size: 'md',
  showClose: true,
  showFooter: false,
  buttonType: 'OkCancel',
  confirmText: '확인',
  cancelText: '취소',
  confirmVariant: 'default',
  confirmDisabled: false,
  closeOnOverlayClick: true,
  closeOnEscape: true,
  resizable: true,
  minSize: 240,
})

const emit = defineEmits<{
  'update:open': [value: boolean]
  confirm: []
  cancel: []
  resize: [size: number]
  'resize-end': [size: number]
}>()

const isHorizontal = computed(() => props.side === 'right' || props.side === 'left')

const sizeClass = computed(() => {
  if (isHorizontal.value) {
    const map: Record<NonNullable<JDrawerProps['size']>, string> = {
      sm: 'sm:max-w-sm',
      md: 'sm:max-w-md',
      lg: 'sm:max-w-lg',
      full: 'sm:max-w-full',
    }
    return map[props.size]
  } else {
    const map: Record<NonNullable<JDrawerProps['size']>, string> = {
      sm: 'max-h-[25vh]',
      md: 'max-h-[50vh]',
      lg: 'max-h-[75vh]',
      full: 'max-h-full',
    }
    return map[props.size]
  }
})

// ── 리사이즈 상태 ──────────────────────────────────────────────────────────
const handleRef = ref<HTMLElement | null>(null)
const currentSize = ref<number | null>(null)
const isResizing = ref(false)

function getPanel(): HTMLElement | null {
  return handleRef.value?.parentElement ?? null
}

function applySizeToPanel(size: number) {
  const panel = getPanel()
  if (!panel) return
  if (isHorizontal.value) {
    panel.style.width = `${size}px`
    panel.style.maxWidth = 'none'
  } else {
    panel.style.height = `${size}px`
    panel.style.maxHeight = 'none'
  }
}

// defaultSize 보관
watch(
  () => props.defaultSize,
  (val) => {
    if (props.resizable && val != null && currentSize.value == null) {
      currentSize.value = val
    }
  },
  { immediate: true },
)

// 재오픈 시 마지막 크기 복원
watch(
  () => props.open,
  async (isOpen) => {
    if (!isOpen || !props.resizable) return
    await nextTick()
    const restore = currentSize.value ?? props.defaultSize
    if (restore != null) applySizeToPanel(restore)
  },
  { immediate: true },
)

const handlePositionClass = computed(() => {
  switch (props.side) {
    case 'right':
      return 'left-0 top-0 h-full w-2 cursor-ew-resize'
    case 'left':
      return 'right-0 top-0 h-full w-2 cursor-ew-resize'
    case 'top':
      return 'bottom-0 left-0 w-full h-2 cursor-ns-resize'
    case 'bottom':
      return 'top-0 left-0 w-full h-2 cursor-ns-resize'
    default:
      return 'left-0 top-0 h-full w-2 cursor-ew-resize'
  }
})

function resolvedMax() {
  if (props.maxSize != null) return props.maxSize
  return isHorizontal.value
    ? Math.floor(window.innerWidth * 0.95)
    : Math.floor(window.innerHeight * 0.95)
}

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max)
}

function measureCurrentSize(): number {
  const panel = handleRef.value?.parentElement
  if (!panel) return isHorizontal.value ? 448 : Math.floor(window.innerHeight * 0.5)
  const rect = panel.getBoundingClientRect()
  return isHorizontal.value ? rect.width : rect.height
}

let startPos = 0
let startSize = 0

function onPointerMove(e: PointerEvent) {
  const pos = isHorizontal.value ? e.clientX : e.clientY
  const delta = pos - startPos
  // right/bottom 방향: 안쪽으로 끌면 크기 증가이므로 delta 부호 반전
  const sign = props.side === 'right' || props.side === 'bottom' ? -1 : 1
  const next = clamp(startSize + sign * delta, props.minSize, resolvedMax())
  currentSize.value = next
  applySizeToPanel(next)
  emit('resize', next)
}

function onPointerUp() {
  isResizing.value = false
  window.removeEventListener('pointermove', onPointerMove)
  if (currentSize.value != null) emit('resize-end', currentSize.value)
}

function onHandlePointerDown(e: PointerEvent) {
  ;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
  isResizing.value = true
  startPos = isHorizontal.value ? e.clientX : e.clientY
  startSize = currentSize.value ?? measureCurrentSize()
  window.addEventListener('pointermove', onPointerMove)
  window.addEventListener('pointerup', onPointerUp, { once: true })
}

onUnmounted(() => {
  window.removeEventListener('pointermove', onPointerMove)
  window.removeEventListener('pointerup', onPointerUp)
})

// ── 기존 핸들러 ────────────────────────────────────────────────────────────
const onOpenChange = (value: boolean) => {
  emit('update:open', value)
}

const onEscapeKeyDown = (event: Event) => {
  if (!props.closeOnEscape) event.preventDefault()
}

const onPointerDownOutside = (event: Event) => {
  if (!props.closeOnOverlayClick) event.preventDefault()
}

const handleConfirm = () => {
  emit('confirm')
}

const handleCancel = () => {
  emit('cancel')
  emit('update:open', false)
}
</script>

<style scoped>
.jdrawer-resize-handle {
  position: absolute;
  z-index: 10;
  background: transparent;
  transition: background 0.15s ease;
}

.jdrawer-resize-handle::after {
  content: '';
  position: absolute;
  background: hsl(var(--border));
  transition:
    background 0.15s ease,
    width 0.12s ease,
    height 0.12s ease;
}

/* 가로 핸들(left/right side): 세로 선 */
.jdrawer-resize-handle.cursor-ew-resize::after {
  top: 0;
  bottom: 0;
  width: 1px;
  left: 50%;
  transform: translateX(-50%);
}

/* 세로 핸들(top/bottom side): 가로 선 */
.jdrawer-resize-handle.cursor-ns-resize::after {
  left: 0;
  right: 0;
  height: 1px;
  top: 50%;
  transform: translateY(-50%);
}

/* 그립 도트 인디케이터 */
.jdrawer-resize-handle::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 3px;
  height: 20px;
  background-image: radial-gradient(
    circle,
    hsl(var(--muted-foreground) / 0.5) 1px,
    transparent 1px
  );
  background-size: 3px 6px;
  background-repeat: repeat-y;
  border-radius: 2px;
  opacity: 0.5;
  transition: opacity 0.15s ease;
  pointer-events: none;
  z-index: 11;
}

.jdrawer-resize-handle.cursor-ns-resize::before {
  width: 20px;
  height: 3px;
  background-size: 6px 3px;
  background-repeat: repeat-x;
  background-image: radial-gradient(
    circle,
    hsl(var(--muted-foreground) / 0.5) 1px,
    transparent 1px
  );
}

/* hover */
.jdrawer-resize-handle:hover {
  background: hsl(var(--primary) / 0.04);
}

.jdrawer-resize-handle:hover::after {
  background: hsl(var(--primary) / 0.55);
}

.jdrawer-resize-handle.cursor-ew-resize:hover::after {
  width: 2px;
}

.jdrawer-resize-handle.cursor-ns-resize:hover::after {
  height: 2px;
}

.jdrawer-resize-handle:hover::before {
  opacity: 1;
}

/* active / drag */
.jdrawer-resize-handle:active {
  background: hsl(var(--primary) / 0.06);
}

.jdrawer-resize-handle:active::after {
  background: hsl(var(--primary));
}

.jdrawer-resize-handle.cursor-ew-resize:active::after {
  width: 2px;
}

.jdrawer-resize-handle.cursor-ns-resize:active::after {
  height: 2px;
}

.jdrawer-resize-handle:active::before {
  opacity: 1;
  background-image: radial-gradient(circle, hsl(var(--primary) / 0.65) 1px, transparent 1px);
}
</style>
